<template>
    <div>
        <template v-if="!isAuth">
            <p>Please login or register to continue.</p>
            <a :href="'/login?return=' + currentUrl" class="btn btn-primary">Login</a>
            <a :href="'/register?return=' + currentUrl" class="btn btn-primary">Register</a>
        </template>
        <template v-else>
            <template v-if="hardError">
                <b-alert variant="danger" :show="true" class="mb-2">
                    Sorry, an unknown error has occurred. This sometimes happens if we sell out. Please refresh the page.
                </b-alert>
            </template>
            <template v-else>
                <template v-if="!isShowUpdatePaymentMethod">
                    <b-alert variant="warning" :show="feedbackProduct.isUserEnrolled" class="mb-4">
                        You have already bought feedback with this artist. If you want to get more feedback on the same track
                        or a completely different track then repeat the process below.
                        You can find your existing feedback on the <a href="/artist-feedback/yours">Your Feedback</a> section.
                    </b-alert>

                    <b-alert variant="danger" :show="hasError" class="mb-2">{{ error }}</b-alert>

                    <div class="text-center">
                        <div class="mb-1">
                            <template v-if="amountToPay == 0">FREE</template>
                            <template v-else>£{{ amountToPay }}</template>
                        </div>

                        <template v-if="paymentMethod">
                            <div class="my-3">
                                <div class="d-inline-block text-center">
                                    <div class="payment-btn-wrapper">
                                        <span class="text-monospace ml-3">
                                            <span class="mr-3">
                                                {{ paymentMethod.card_type.charAt(0).toUpperCase() + paymentMethod.card_type.slice(1) }}
                                            </span>
                                            **** **** **** {{ paymentMethod.last4 }}
                                        </span>
                                    </div>

                                    <div class="mb-3 mt-1">
                                        <b-button
                                            variant="link"
                                            @click="isShowUpdatePaymentMethod = true"
                                            v-if="!isShowUpdatePaymentMethod"
                                        >
                                            Update payment method
                                        </b-button>
                                    </div>
                                </div>
                            </div>
                        </template>
                        <template v-else>
                            <b-button
                                variant="link"
                                @click="isShowUpdatePaymentMethod = true"
                                v-if="!isShowUpdatePaymentMethod"
                            >
                                Add payment method
                            </b-button>
                        </template>

                        <hr />

                        <div class="row justify-content-center">
                            <div class="col-md-6 text-center">
                                <div class="form-group">
                                    <label for="discountCode">Have a discount code? Enter it below.</label>
                                    <div class="input-group">
                                        <input
                                            type="text"
                                            class="form-control"
                                            id="discountCode"
                                            v-model="discountCode"
                                        />
                                        <div class="input-group-append">
                                            <b-button variant="primary" class="px-3" @click="handleDiscount">
                                                Apply
                                            </b-button>
                                        </div>
                                    </div>
                                    <b-alert variant="success" :show="discountSuccessMsg.length > 0">
                                        {{ discountSuccessMsg }}
                                    </b-alert>
                                </div>
                            </div>
                        </div>

                        <template v-if="paymentMethod">
                            <b-form-checkbox
                                v-model="tandc"
                                name="tandc"
                                value="accepted"
                                unchecked-value="not_accepted"
                                class="my-3"
                            >
                                By purchasing this feedback I consent to PLAYvirtuoso
                                <a
                                    href="https://playvirtuoso.com/tandc/"
                                    target="_blank"
                                    class="text-underline"
                                >terms and conditions</a
                                >.
                            </b-form-checkbox>

                            <b-button variant="success" :disabled="isBusy" @click="handleEnrol">Buy Now</b-button>
                        </template>
                    </div>
                </template>
                <template v-else>
                    <div class="mb-2 text-center font-weight-bold">
                        If you have a voucher code this can be added after you have added your payment method.
                    </div>
                    <card-update-component
                        v-if="isShowUpdatePaymentMethod"
                        :user="user"
                        :is-inline="true"
                        :with-cancel="allowCancelling"
                        :amount="amountToPaySubUnit"
                    ></card-update-component>
                </template>
            </template>
        </template>
    </div>
</template>

<script>
import { globalEventFire } from '../tracker.js';

export default {
    props: {
        artist: {
            type: Object,
            required: true,
        },
        feedbackProduct: {
            type: Object,
            required: true,
        },
        isNewUser: {
            type: Boolean,
            required: false,
        },
    },

    data() {
        return {
            stripeAPIToken: document
                .querySelector('meta[name="stripe-key"]')
                .getAttribute('content'),
            isBusy: false,
            hardError: false,
            hasError: false,
            error: '',
            currentUrl: '',
            tandc: 'not_accepted',
            isShowUpdatePaymentMethod: false,
            paymentMethod: null,
            isFree: false,
            isAuth: false,
            isSubscribed: false,
            discountCode: '',
            enteredDiscountCode: '',
            discountSuccessMsg: '',
            discountPercentage: 0,
            discountAmount: 0,
            priceAfterDiscount: 0,
            appliedDiscountCodeId: null,
            isFreeDiscountCode: false,
            allowCancelling: false,
            userTier: null,
        };
    },

    computed: {
        // Amount to pay
        amountToPay: function () {
            let amt = 0;

            if (this.priceAfterDiscount > 0) {
                amt = this.priceAfterDiscount;
            } else {
                if (this.isFreeDiscountCode) {
                    amt = 0;
                } else {
                    if (this.isSubscribed && this.userTier == 'premium') {
                        amt = this.feedbackProduct.price_subscription;
                    } else {
                        amt = this.feedbackProduct.price;
                    }
                }
            }

            // Ensure amt is a number and round to two decimal places
            amt = Math.round(parseFloat(amt) * 100) / 100;

            return amt;
        },

        amountToPaySubUnit: function () {
            let amt = 0;

            if (this.priceAfterDiscount > 0) {
                amt = this.priceAfterDiscount;
            } else {
                if (this.isFreeDiscountCode) {
                    amt = 0;
                } else {
                    if (this.isSubscribed && this.userTier == 'premium') {
                        amt = this.feedbackProduct.price_subscription;
                    } else {
                        amt = this.feedbackProduct.price;
                    }
                }
            }

            // Ensure amt is a number
            amt = parseFloat(amt);

            // Convert to pence (multiply by 100) and round to nearest integer
            let amtInPence = Math.round(amt * 100);

            return amtInPence;
        },


    },

    mounted() {
        this.currentUrl = window.location.pathname;

        // User logged in?
        if (window.virtuoso) {
            if (window.virtuoso.is_auth) {
                this.isAuth = true;
                this.user = window.virtuoso.user;
                this.isSubscribed = window.virtuoso.is_subscribed;
                this.userTier = window.virtuoso.tier;
            }
        }

        // Load stripe
        this.includeScript(
            'js.stripe.com/v3/',
            function () {
                this.configureStripe();
            }.bind(this)
        );

        this.loadPaymentMethod();

        // When payment methods have been updated refresh that section
        Bus.$on('updatedPaymentMethods', (d) => {
            this.loadPaymentMethod();
            this.isShowUpdatePaymentMethod = false;
        });

        // Handle cancellation of payment method update
        Bus.$on('cancelPaymentMethod', () => {
            this.loadPaymentMethod();
            this.isShowUpdatePaymentMethod = false;
        });
    },

    methods: {
        includeScript(URL, callback) {
            let documentTag = document,
                tag = 'script',
                object = documentTag.createElement(tag),
                scriptTag = documentTag.getElementsByTagName(tag)[0];
            object.src = '//' + URL;
            if (callback) {
                object.addEventListener(
                    'load',
                    function (e) {
                        callback(null, e);
                    },
                    false
                );
            }
            scriptTag.parentNode.insertBefore(object, scriptTag);
        },

        /*
            Configures Stripe by setting up the elements and
            creating the card element.
        */
        configureStripe() {
            this.stripe = Stripe(this.stripeAPIToken);
        },

        //
        loadPaymentMethod() {
            // Load payment methods
            this.isBusy = true;
            this.isLoadingPaymentMethods = true;
            axios.get('/api/me/payment_method').then((response) => {
                this.paymentMethod = response.data;
                this.isBusy = false;

                if (this.paymentMethod) {
                    this.isShowUpdatePaymentMethod = false;
                    this.allowCancelling = true;
                } else {
                    this.isShowUpdatePaymentMethod = true;
                    this.allowCancelling = false;
                }
            });
        },

        // Handle the discount - called when a user clicks/enters a discount code
        handleDiscount() {
            this.isBusy = true;
            axios
                .post('/api/discounts/feedback/' + this.feedbackProduct.id + '/check', {
                    code: this.discountCode,
                })
                .then((response) => {
                    if (response.data.success) {
                        this.isBusy = false;
                        this.hasError = false;
                        this.error = '';
                        this.discountPercentage = response.data.percentage;
                        this.discountSuccessMsg =
                            'A ' +
                            this.discountPercentage +
                            '% discount has been applied to your order.';
                        this.discountAmount = response.data.discountAmount;
                        this.priceAfterDiscount = response.data.priceAfterDiscount;
                        this.appliedDiscountCodeId = response.data.id;
                        this.enteredDiscountCode = this.discountCode;

                        if (this.discountPercentage == 100) {
                            this.isFreeDiscountCode = true;
                        } else {
                            this.isFreeDiscountCode = false;
                        }
                    }

                    if (response.data.error) {
                        this.hasError = true;
                        this.error = response.data.error;
                        this.isBusy = false;
                        this.discountPercentage = 0;
                        this.discountSuccessMsg = '';
                        this.discountAmount = 0;
                        this.priceAfterDiscount = 0;
                        this.appliedDiscountCodeId = null;
                        this.isFreeDiscountCode = false;
                    }
                })
                .catch((error) => {
                    this.hasError = true;
                    this.error = 'An unexpected error occurred.';
                    this.isBusy = false;
                });
        },

        // Handle enrolment
        handleEnrol() {
            if (this.tandc == 'not_accepted') {
                this.hasError = true;
                this.error = 'You must accept the terms and conditions to continue.';
            } else {
                this.hasError = false;
                this.error = '';

                this.isBusy = true;

                if (this.isFree || this.isFreeDiscountCode) {
                    console.log('fireEnrolment');
                    this.fireEnrolment();
                } else {
                    console.log('handlePayment');
                    this.handlePayment();
                }
            }
        },

        // Actually enrol them
        fireEnrolment() {
            // Fire it
            let newUserAppend = this.isNewUser
                ? '?newUser=true&enrolled=true'
                : '?enrolled=true';
            axios
                .post(
                    '/api/feedback_products/' +
                    this.feedbackProduct.id +
                    '/enrol' +
                    (this.appliedDiscountCodeId
                        ? '?discountCodeId=' + this.appliedDiscountCodeId
                        : '')
                )
                .then((response) => {
                    if (response.data.success) {
                        // Fire analytics event
                        this.fireAnalytics(response.data.enrolment_id);
                        location.href = response.data.redirect + newUserAppend;
                    }

                    if (response.data.error) {
                        this.hasError = true;
                        this.error = response.data.error;
                        this.isBusy = false;
                    }
                })
                .catch((error) => {
                    console.log(error);
                    this.hasError = true;
                    this.error = 'An unexpected error occurred.';
                    this.isBusy = false;
                });
        },

        // Attempt payment and then enrol
        handlePayment() {
            axios
                .post(
                    '/api/feedback_products/' +
                    this.feedbackProduct.id +
                    '/pay' +
                    (this.appliedDiscountCodeId
                        ? '?discountCodeId=' + this.appliedDiscountCodeId
                        : '')
                )
                .then((response) => {
                    if (response.data.success) {
                        this.successfulPaymentRedirect(
                            response.data.metadata.enrolment_id ?? 0,
                            response.data.redirect ?? ''
                        );
                    }

                    if (response.data.error) {
                        this.hasError = true;
                        this.error = response.data.error;
                        this.isBusy = false;
                    }

                    if (response.data.status) {
                        let payment_intent = response.data;

                        if (payment_intent) {
                            const { client_secret, status } = payment_intent;
                            if (status === 'requires_action') {
                                this.stripe.confirmCardPayment(client_secret).then((result) => {
                                    if (result.error) {
                                        // Display error message in your UI.
                                        // The card was declined (i.e. insufficient funds, card has expired, etc)
                                        this.hasError = true;
                                        this.error = result.error.message;
                                        this.isBusy = false;
                                    } else {
                                        // Redirect after a timeout to give the webhook time to run
                                        this.successfulPaymentRedirect(
                                            response.data.metadata.enrolment_id ?? 0,
                                            response.data.redirect ?? ''
                                        );
                                    }
                                });
                            } else if (status === 'succeeded') {
                                this.successfulPaymentRedirect(
                                    response.data.metadata.enrolment_id ?? 0,
                                    response.data.redirect ?? ''
                                );
                            } else {
                                this.hasError = true;
                                this.error =
                                    'An error was encountered.  Error code #FAS2135';
                            }
                        }
                    }
                })
                .catch((error) => {
                    this.hasError = true;
                    this.error = 'An unexpected error occurred.';
                    this.isBusy = false;
                });
        },

        // When payment is successful redirect after firing analytics
        successfulPaymentRedirect(enrolment_id, redirect) {
            let newUserAppend = this.isNewUser
                ? '&newUser=true&enrolled=true'
                : '&enrolled=true';

            // Fire success
            this.fireAnalytics(enrolment_id);

            setTimeout(() => {
                location.href = redirect + '?' + newUserAppend;
            }, 2000);
        },

        // Fire analytics
        fireAnalytics(enrolment_id) {
            globalEventFire('Purchase', 'Feedback', {
                item_id: this.feedbackProduct.id,
                category: 'Feedback',
                name: this.feedbackProduct.name + '(' + this.artist.name + ')',
                num_items: 1,
                amount: this.amountToPay,
                enrolment_id: enrolment_id,
            });
        },
    },
};
</script>
